/**
 * reportFactCheck — ITT-3 follow-up.
 *
 * Pure, isolated fact-check pass that compares the narrative claims on a
 * generated report against the gen-time provider data persisted on the
 * report itself (`report.datasets`) and the time-series. Mirrors the
 * inline numeric-pattern fact-check that runs as Phase 4 of report
 * generation in `siteRagService.generateReport` (search:
 * `Phase 4: Fact-Check Gate`).
 *
 * Why this module exists:
 *
 *   ITT-3 surfaces a "Retry fact-check" button in the Quality tab when
 *   the original generation produced no `factCheck` (degraded mode,
 *   empty SED, or a Phase 4 error). The button must call a function
 *   that produces the same shape of result the gen-path would have
 *   produced (`{ verified, unverified, contradictions, score }`),
 *   without rerunning report generation or re-querying providers.
 *
 *   The original fact-check uses the live `SiteEvidenceDocument` (a
 *   richer per-section structure with `status: 'verified' | ...`) that
 *   is recompiled at gen-time and NOT persisted on the report. The
 *   persisted equivalent on the report is the flatter
 *   `report.datasets` (with no per-section status flag — its presence
 *   IS the verification signal). This module synthesises a minimal
 *   SED-shaped view from `report.datasets` that is sufficient to drive
 *   the same numeric pattern matches the gen-path runs.
 *
 * Engineering notes (CLAUDE.md):
 *
 *   - This module is intentionally a duplicate of the gen-path checks
 *     rather than a refactor of `siteRagService.ts:1795-1917`. The
 *     gen-time logic is solver-adjacent (it gates whether
 *     `report.factCheck` is populated; downstream UI badges depend on
 *     it). Preserving its exact behaviour matters more than DRY here,
 *     so the retry path duplicates the patterns + thresholds verbatim.
 *     If a future refactor unifies the two, the unit tests on
 *     `runFactCheck` pin the inputs/outputs both paths must satisfy.
 *
 *   - Reproducibility: the retry only consumes data that was persisted
 *     ON the report at gen-time (`datasets` + `timeSeries` + narrative
 *     fields). It does NOT re-query providers and does NOT recompile
 *     the live SED, so a fact-check produced today against a frozen
 *     report is identical to one produced tomorrow — even if the
 *     underlying site's provider data has since changed.
 */

import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { hashCanonicalJson } from '../lib/reportGeneration/reportContractHash';
import type { SiteRagReport, TimeSeriesData } from '../types/siteRag';
// C0-GEN Wave 2 (spec fix #1) — the editorial re-voicing must flow through the
// numeric fact-check, not just the arithmetic density check. Reuse the shared
// flattener so the fact-check pattern-matches the SAME editorial prose the
// audit gates see (single source of truth).
import { flattenEditorialNarrative } from '../lib/audit/reportTextExtract';
import { checkSiteAreaClaims } from '../lib/audit/siteAreaClaims';

// ── Mini-SED shape ─────────────────────────────────────────────────
// The minimal subset of `SiteEvidenceDocument` the numeric fact-check
// actually reads. `status: 'verified'` ⇒ "we have a real value, check
// the narrative against it"; `'unavailable'` ⇒ "no value, flag any
// specific number in the narrative as a hallucination". The full SED
// has additional statuses (`verified-from-doc`, `verified-from-context`,
// etc.) but the fact-check only branches on verified-vs-unavailable, so
// we collapse the ladder for the retry path.
type SedStatus = 'verified' | 'unavailable';

interface MiniSedSection<T> {
  status: SedStatus;
  data: T | null;
}

interface MiniSeismic {
  pga: number;
  sds: number;
  sd1: number;
  designCategory: string;
}

interface MiniSiteProfile {
  /** May be null when elevation lookups failed at gen-time (EL2). */
  elevationAvg: number | null;
  acres: number | null;
}

interface MiniGrid {
  substationDistKm: number;
}

interface MiniClimate {
  avgWetBulb: number;
  humidity: number;
  peakWetBulb?: number | null;
  tmyMeanWetBulb?: number | null;
}

interface MiniSolar {
  ghi: number;
}

interface MiniAirQuality {
  aqi: number;
}

interface MiniWaterStress {
  stressLevel: string;
}

export interface MiniSed {
  seismic: MiniSedSection<MiniSeismic>;
  siteProfile: MiniSedSection<MiniSiteProfile>;
  grid: MiniSedSection<MiniGrid>;
  climate: MiniSedSection<MiniClimate>;
  solar: MiniSedSection<MiniSolar>;
  airQuality: MiniSedSection<MiniAirQuality>;
  waterStress: MiniSedSection<MiniWaterStress>;
  wind: MiniSedSection<unknown>;
  electricity: MiniSedSection<unknown>;
  connectivity: MiniSedSection<unknown>;
  parcel: MiniSedSection<unknown>;
}

export interface FactCheckCompletion {
  version: 'numeric-fact-check-v1';
  status: 'completed';
  scope: 'recorded-input-numeric-consistency';
  applicableChecks: number;
  sourceSedId: string;
  sourceSedVersion: number;
  narrativeHash: string;
}

export interface FactCheckResult {
  verified: number;
  unverified: number;
  contradictions: string[];
  score: number;
  completion?: FactCheckCompletion;
}

export interface RunFactCheckInput {
  /** Concatenated narrative text — typically `JSON.stringify(geminiData)`
   *  at gen-time, or a synthesised narrative bag at retry time. */
  allText: string;
  sed: MiniSed;
  timeSeries: TimeSeriesData[];
}

/**
 * Run the numeric fact-check pass. Pure — no IO. Mirrors the inline
 * logic at `siteRagService.ts:1795-1917` exactly (same patterns, same
 * tolerances, same unavailable-hallucination checks).
 */
export function runFactCheck({ allText, sed, timeSeries }: RunFactCheckInput): FactCheckResult {
  const contradictions: string[] = [];
  let verified = 0;
  let checked = 0;

  // Helper: check a numeric claim in AI text against SED value.
  const checkNumeric = (pattern: RegExp, sedValue: number, label: string, tolerance: number) => {
    const match = allText.match(pattern);
    if (match) {
      checked++;
      const aiValue = parseFloat(match[1]);
      if (Math.abs(aiValue - sedValue) > tolerance) {
        contradictions.push(`${label}: AI says ${aiValue}, SED has ${sedValue}`);
      } else {
        verified++;
      }
    }
  };

  // Seismic: PGA, SDS, SD1, SDC letter.
  if (sed.seismic.status === 'verified' && sed.seismic.data) {
    const s = sed.seismic.data;
    if (s.pga > 0) checkNumeric(/PGA\s+([\d.]+)/i, s.pga, 'PGA', 0.02);
    checkNumeric(/SDS\s+([\d.]+)/i, s.sds, 'SDS', 0.02);
    checkNumeric(/SD1\s+([\d.]+)/i, s.sd1, 'SD1', 0.02);
    if (allText.includes('SDC') || allText.includes('Design Category')) {
      checked++;
      if (
        allText.includes(`SDC ${s.designCategory}`) ||
        allText.includes(`Category ${s.designCategory}`) ||
        allText.includes(`Category: ${s.designCategory}`)
      ) {
        verified++;
      } else {
        const sdcMatch = allText.match(/SDC\s+([A-F])/);
        if (sdcMatch && sdcMatch[1] !== s.designCategory) {
          contradictions.push(`SDC: AI says ${sdcMatch[1]}, SED has ${s.designCategory}`);
        }
      }
    }
  }

  // Site profile: elevation (EL2 null-guard) + explicitly whole-site acreage.
  if (sed.siteProfile.status === 'verified' && sed.siteProfile.data) {
    const elevAvg = sed.siteProfile.data.elevationAvg;
    if (typeof elevAvg === 'number' && Number.isFinite(elevAvg)) {
      checkNumeric(/(\d{3,4})\s*m\s*ASL/i, elevAvg, 'Elevation', 15);
    }
    if (typeof sed.siteProfile.data.acres === 'number' && Number.isFinite(sed.siteProfile.data.acres) && sed.siteProfile.data.acres > 0) {
      const areaCheck = checkSiteAreaClaims(allText, sed.siteProfile.data.acres);
      checked += areaCheck.checked;
      verified += areaCheck.verified;
      contradictions.push(...areaCheck.contradictions);
    }
  }

  // Grid distance.
  if (sed.grid.status === 'verified' && sed.grid.data) {
    checkNumeric(/substation[^.]*?([\d.]+)\s*km/i, sed.grid.data.substationDistKm, 'Substation distance', 5);
  }

  // Climate — wet bulb accepts EITHER avg OR peak; TMY hottest-hours wet bulb
  // is checked against tmyHottestHours.meanWetBulbC when present; humidity is a
  // simple numeric.
  if (sed.climate.status === 'verified' && sed.climate.data) {
    const c = sed.climate.data;
    const wbMentions = extractWetBulbMentionsCelsius(allText);
    if (wbMentions.length > 0) {
      checked++;
      const tsPeakWb = timeSeries.length > 0
        ? Math.max(...timeSeries.map(ts => ts.wetBulbTemp ?? c.avgWetBulb))
        : c.avgWetBulb;
      const peakWb = typeof c.peakWetBulb === 'number' && Number.isFinite(c.peakWetBulb)
        ? Math.max(tsPeakWb, c.peakWetBulb)
        : tsPeakWb;
      const contradictingWb = wbMentions.find(
        aiWb => Math.abs(aiWb - c.avgWetBulb) > 2 && Math.abs(aiWb - peakWb) > 2,
      );
      if (contradictingWb === undefined) {
        verified++;
      } else {
        contradictions.push(`Wet bulb: AI says ${contradictingWb}, SED has avg ${c.avgWetBulb} / peak ${peakWb.toFixed(1)}`);
      }
    }
    const tmyMeanWb = c.tmyMeanWetBulb;
    if (typeof tmyMeanWb === 'number' && Number.isFinite(tmyMeanWb)) {
      const tmyWbMentions = extractTmyWetBulbMentionsCelsius(allText);
      if (tmyWbMentions.length > 0) {
        checked++;
        const contradictingTmyWb = tmyWbMentions.find(
          aiWb => Math.abs(aiWb - tmyMeanWb) > 0.5,
        );
        if (contradictingTmyWb === undefined) {
          verified++;
        } else {
          contradictions.push(`TMY hottest-hours wet bulb: AI says ${contradictingTmyWb}, SED has ${tmyMeanWb.toFixed(1)}`);
        }
      }
    }
    checkNumeric(/humidity[^.]*?([\d.]+)\s*%/i, c.humidity, 'Humidity', 10);
  }

  // Solar GHI.
  if (sed.solar.status === 'verified' && sed.solar.data) {
    checkNumeric(/GHI[^.]*?([\d.]+)\s*kWh/i, sed.solar.data.ghi, 'Solar GHI', 0.5);
  }

  // AQI.
  if (sed.airQuality.status === 'verified' && sed.airQuality.data) {
    checkNumeric(/AQI\s+(\d+)/i, sed.airQuality.data.aqi, 'AQI', 5);
  }

  // Water stress label appears anywhere in the text — coarse but the
  // gen-path uses the same heuristic.
  if (sed.waterStress.status === 'verified' && sed.waterStress.data) {
    checked++;
    const stressInText = allText.toLowerCase().includes(sed.waterStress.data.stressLevel.toLowerCase());
    if (stressInText) verified++;
  }

  // Hallucination guard: unavailable data presented as fact.
  const unavailableChecks: { key: keyof MiniSed; pattern: RegExp; label: string }[] = [
    { key: 'wind',         pattern: /wind speed[^.]*?[\d.]+\s*m\/s/i, label: 'wind speed' },
    { key: 'electricity',  pattern: /[\d.]+\s*¢\/kWh/i, label: 'electricity rate' },
    { key: 'grid',         pattern: /substation[^.]*?[\d.]+\s*km/i, label: 'substation distance' },
    { key: 'connectivity', pattern: /nearest IX[^.]*?[\d.]+\s*km/i, label: 'IX distance' },
    { key: 'parcel',       pattern: /zoning[:\s]+(Industrial|Residential|Commercial|Mixed)/i, label: 'parcel zoning' },
  ];
  for (const { key, pattern, label } of unavailableChecks) {
    const sec = sed[key];
    if (sec && sec.status === 'unavailable' && pattern.test(allText)) {
      contradictions.push(`${label} data UNAVAILABLE in SED but AI reports specific value`);
    }
  }

  return {
    verified,
    unverified: checked - verified - contradictions.length,
    contradictions,
    score: checked > 0 ? verified / Math.max(1, checked) : 1.0,
  };
}

// ── Mini-SED synthesis from a persisted report ─────────────────────

/**
 * Build a `MiniSed` from the dataset bag persisted on a generated
 * report. The presence of a dataset field is treated as "verified"; an
 * absent field collapses to `status: 'unavailable'` so the
 * hallucination guard still fires for narratives that mention values
 * the original gen-path couldn't pin down. We do NOT promote partial
 * data to `'verified'` — if `report.datasets.seismic` is missing
 * entirely, the seismic block in the narrative simply isn't checked
 * (matching the gen-path behaviour where an unverified SED section is
 * skipped).
 */
export function synthesizeMiniSed(datasets: SiteRagReport['datasets']): MiniSed {
  const d = datasets;
  const verifiedOrUnavailable = <T,>(value: T | undefined | null): MiniSedSection<T> =>
    value != null ? { status: 'verified', data: value } : { status: 'unavailable', data: null };

  return {
    seismic: verifiedOrUnavailable(
      d?.seismic
        ? {
            pga: d.seismic.pga,
            sds: d.seismic.sds,
            sd1: d.seismic.sd1,
            designCategory: d.seismic.designCategory,
          }
        : null,
    ),
    siteProfile: verifiedOrUnavailable(
      d?.land
        ? {
            // ParcelData on the report has a numeric elevation; treat 0 as a
            // real reading (legacy reports) but null on the parcel data field
            // when the gen-time SED actually flagged it absent. The dataset
            // record collapses null→0 (legacy schema), so we accept the value
            // verbatim; consumers that need the EL2 null-guard upstream are
            // the gen-path, not the retry path.
            elevationAvg: typeof d.land.elevation === 'number' && Number.isFinite(d.land.elevation)
              ? d.land.elevation
              : null,
            acres: d.land.acres,
          }
        : null,
    ),
    grid: verifiedOrUnavailable(
      d?.grid ? { substationDistKm: d.grid.substationDist } : null,
    ),
    climate: verifiedOrUnavailable(
      d?.climate
        ? {
            avgWetBulb: d.climate.avgWetBulb,
            humidity: d.climate.humidity,
            peakWetBulb:
              typeof (d.climate as unknown as { peakWetBulb?: unknown }).peakWetBulb === 'number' &&
              Number.isFinite((d.climate as unknown as { peakWetBulb?: number }).peakWetBulb)
                ? (d.climate as unknown as { peakWetBulb: number }).peakWetBulb
                : null,
            tmyMeanWetBulb:
              typeof (d.climate as unknown as { tmyHottestHours?: { meanWetBulbC?: unknown } }).tmyHottestHours?.meanWetBulbC === 'number' &&
              Number.isFinite((d.climate as unknown as { tmyHottestHours: { meanWetBulbC: number } }).tmyHottestHours.meanWetBulbC)
                ? (d.climate as unknown as { tmyHottestHours: { meanWetBulbC: number } }).tmyHottestHours.meanWetBulbC
                : null,
          }
        : null,
    ),
    solar: verifiedOrUnavailable(d?.solar ? { ghi: d.solar.ghi } : null),
    airQuality: verifiedOrUnavailable(d?.airQuality ? { aqi: d.airQuality.aqi } : null),
    waterStress: verifiedOrUnavailable(
      d?.climate ? { stressLevel: d.climate.waterStress } : null,
    ),
    wind: verifiedOrUnavailable(d?.wind ?? null),
    electricity: verifiedOrUnavailable(d?.electricity ?? null),
    connectivity: verifiedOrUnavailable(d?.connectivity ?? null),
    parcel: verifiedOrUnavailable(d?.parcel ?? null),
  };
}

/**
 * Build the narrative text bag the fact-check pattern matches against.
 * The gen-path uses `JSON.stringify(geminiData)`; we approximate by
 * stringifying the report itself (minus binary/large irrelevant
 * fields). Pattern matches are unit-anchored ("PGA 0.43", "SDS 1.2",
 * etc.) so the surrounding JSON noise doesn't introduce false hits.
 */
export function buildNarrativeText(report: SiteRagReport): string {
  // Keep only the human-narrative sections — the dataset bag itself
  // (provider data) is excluded so the fact-check doesn't trivially
  // "verify" against the same numbers it's checking the narrative
  // against (that would always pass and tell us nothing). Listing the
  // fields explicitly is safer than a blacklist: if a future field is
  // added that should participate, the caller updates this list
  // deliberately.
  const narrativeFields = {
    executive: report.executive,
    stakeholderMap: report.stakeholderMap,
    perBtmSentiment: report.perBtmSentiment,
    themeDeepDives: report.themeDeepDives,
    trendAnalysis: report.trendAnalysis,
    comparableSiteBenchmark: report.comparableSiteBenchmark,
    mitigationPlaybook: report.mitigationPlaybook,
    sentimentAppendix: report.sentimentAppendix,
    summary: report.summary,
    executiveSummary: report.executiveSummary,
    insights: report.insights,
    risks: report.risks,
    recommendations: report.recommendations,
    btmOptions: (report as { btmOptions?: unknown }).btmOptions,
    connectionRisks: (report as { connectionRisks?: unknown }).connectionRisks,
    developmentRisks: (report as { developmentRisks?: unknown }).developmentRisks,
    developmentPathway: report.developmentPathway,
    climateSevereWeather: report.climateSevereWeather,
    infrastructureInterconnection: report.infrastructureInterconnection,
    sitingNotes: report.sitingNotes,
    appendix: report.appendix,
    // C0-GEN Wave 2 (spec fix #1) — the generated editorial re-voicing.
    // Empty string on flag-OFF / legacy reports ⇒ no new text ⇒ the
    // fact-check result is byte-identical to today. When present, the
    // re-voiced numbers are pattern-matched against the SAME mini-SED, so a
    // hallucinated figure in the editorial prose is caught here too.
    editorialNarrative: flattenEditorialNarrative(report),
  };
  return JSON.stringify(narrativeFields);
}

/**
 * Recompute the fact-check for an already-generated report using only
 * the data persisted on the report. Idempotent — calling this twice in
 * a row produces identical results.
 */
export function rerunReportFactCheck(report: SiteRagReport): FactCheckResult {
  const sed = synthesizeMiniSed(report.datasets);
  const allText = buildNarrativeText(report);
  return runFactCheck({ allText, sed, timeSeries: report.timeSeries ?? [] });
}

/** The typed path checks the final narrative against the actual frozen source,
 * not dataset presence/defaults. This checks recorded-number consistency only;
 * sentiment attribution, source completeness and independent truth stay in QA. */
export function runFrozenReportFactCheck(report: SiteRagReport, sed: SiteEvidenceDocument): FactCheckResult {
  const fromSource = <T,>(section: { status: string; data: T | null } | undefined): MiniSedSection<T> =>
    typeof section?.status === 'string' && section.status.startsWith('verified') && section.data != null
      ? { status: 'verified', data: section.data } : { status: 'unavailable', data: null };
  const mini: MiniSed = {
    seismic: fromSource(sed.seismic), siteProfile: fromSource(sed.siteProfile),
    grid: fromSource(sed.grid),
    climate: typeof sed.climate?.status === 'string' && sed.climate.status.startsWith('verified') && sed.climate.data != null
      ? {
          status: 'verified',
          data: {
            ...sed.climate.data,
            peakWetBulb:
              typeof sed.climate.data.peakWetBulb === 'number' && Number.isFinite(sed.climate.data.peakWetBulb)
                ? sed.climate.data.peakWetBulb
                : null,
            tmyMeanWetBulb:
              typeof sed.climate.data.tmyHottestHours?.meanWetBulbC === 'number' &&
              Number.isFinite(sed.climate.data.tmyHottestHours.meanWetBulbC)
                ? sed.climate.data.tmyHottestHours.meanWetBulbC
                : null,
          },
        }
      : { status: 'unavailable', data: null },
    solar: fromSource(sed.solar),
    airQuality: fromSource(sed.airQuality), waterStress: fromSource(sed.waterStress),
    wind: fromSource(sed.wind), electricity: fromSource(sed.electricity),
    connectivity: fromSource(sed.connectivity), parcel: fromSource(sed.parcel),
  };
  const allText = buildNarrativeText(report);
  // The legacy checker tests the stress label whenever that dataset exists.
  // For scoped completion, unrelated reports do not acquire a water check.
  if (!/water\s+(?:stress|risk)/i.test(allText)) mini.waterStress = { status: 'unavailable', data: null };
  const result = runFactCheck({ allText, sed: mini, timeSeries: report.timeSeries ?? [] });
  const unverified = Math.max(0, result.unverified);
  const applicableChecks = result.verified + unverified + result.contradictions.length;
  return { ...result, unverified, score: applicableChecks > 0 ? result.verified / applicableChecks : result.score, completion: { version: 'numeric-fact-check-v1', status: 'completed',
    scope: 'recorded-input-numeric-consistency',
    applicableChecks,
    sourceSedId: sed.id, sourceSedVersion: sed.version, narrativeHash: hashCanonicalJson(allText) } };
}

/** N/A is an explicitly completed numeric check with no applicable claims,
 * never a perfect verification score or a source-coverage certificate. */
export function numericFactCheckNotApplicable(result: SiteRagReport['factCheck']): boolean {
  return result?.completion?.version === 'numeric-fact-check-v1'
    && result.completion.status === 'completed'
    && result.completion.scope === 'recorded-input-numeric-consistency'
    && result.completion.applicableChecks === 0
    && result.verified === 0 && result.unverified === 0 && result.contradictions.length === 0;
}

export function factCheckDisplaySummary(result: NonNullable<SiteRagReport['factCheck']>): string {
  if (numericFactCheckNotApplicable(result)) return 'Completed; no applicable numeric checks (not source or sentiment verification)';
  if (result.completion) return `${result.verified}/${result.completion.applicableChecks} numeric checks match recorded inputs; ${result.contradictions.length} contradiction(s)`;
  return `${result.verified}/${result.verified + result.unverified} verified`;
}

function extractWetBulbMentionsByScope(text: string): { standard: number[]; tmyHottestHours: number[] } {
  const standard: number[] = [];
  const tmyHottestHours: number[] = [];
  const seenStandard = new Set<number>();
  const seenTmy = new Set<number>();

  const addStandard = (val: number) => {
    if (Number.isFinite(val) && !seenStandard.has(val)) {
      seenStandard.add(val);
      standard.push(val);
    }
  };
  const addTmy = (val: number) => {
    if (Number.isFinite(val) && !seenTmy.has(val)) {
      seenTmy.add(val);
      tmyHottestHours.push(val);
    }
  };

  // Strip inline citation markers like "[verified Ref 2]" or "[Ref 12]" so bracket splitting
  // does not sever a clause mid-sentence.
  const cleanedText = text.replace(/\[(?:verified\s+)?Ref\s+\d+\]/gi, ' ');

  // Split on sentence boundaries, semicolons, pipes, newlines, JSON delimiters, and TMY hot-tail contrast clauses
  const clauses = cleanedText.split(
    /(?<=[.!?])\s+|,\s+(?=(?:(?:while|whereas|and|though|although)\s+)?(?:in\s+(?:the\s+)?)?[^.;\n]{0,75}\b(?:hottest\s+\d+\s+of\s+8,?760\s+hours|TMY\s+hottest(?:[-\s]+\d+[-\s]*hour(?:s)?|[-\s]+hours)?|paired\s+mean\s+(?:dry|wet)[-\s]*bulb)\b)|[\n;"{}\[\]|]+/i,
  );

  const tmyClauseRe =
    /\b(?:hottest\s+\d+\s+of\s+8,?760\s+hours|TMY\s+hottest(?:[-\s]+\d+[-\s]*hour(?:s)?|[-\s]+hours)?|paired\s+mean\s+(?:dry[-\s]*bulb\s+[^.;\n]*?\bwet[-\s]*bulb|wet[-\s]*bulb)|(?:combining|using|pairing|comparing)\s+(?:that|this|the\s+same)\s+\d+(?:\.\d+)?\s*°\s*C\s+(?:(?:paired|TMY)\s+)?mean\s+wet[-\s]*bulb\b)/i;
  const tmyModifierRe = /\b(?:TMY|hottest(?:[-\s]+\d+[-\s]*hour(?:s)?|[-\s]+hours|\s+\d+\s+of\s+8,?760\s+hours)?|paired\s+mean)\b/i;

  for (const clause of clauses) {
    if (!/wet[-\s]*bulb/i.test(clause)) continue;
    const isTmyClause = tmyClauseRe.test(clause);

    // 1. Value preceding wet-bulb label: e.g., "17.2°C wet-bulb", "24.8 °C (peak wet bulb)", "24.2 °C TMY hottest-hours coincident wet bulb"
    const preNumberRe =
      /(\d+(?:\.\d+)?)\s*°\s*C\s*(?:\(\s*)?(?:(?:TMY|hottest(?:[-\s]+\d+[-\s]*hour(?:s)?|[-\s]+hours)?|coincident|paired|avg|average|mean|peak|max|maximum|design|monthly|summer|ambient|historical|screening)\s+)*wet[-\s]*bulb\b(?!\s+depression\b)/gi;
    let m: RegExpExecArray | null;
    while ((m = preNumberRe.exec(clause)) !== null) {
      const prefix = clause.slice(Math.max(0, m.index - 28), m.index);
      if (/\bdry[-\s]*bulb\s*(?:of|at|is|:)?\s*$/i.test(prefix)) continue;
      const val = parseFloat(m[1]);
      if (isTmyClause || tmyModifierRe.test(m[0]) || tmyModifierRe.test(prefix)) {
        addTmy(val);
      } else {
        addStandard(val);
      }
    }

    // 2. Wet-bulb label preceding value(s): e.g., "wet-bulb temperature of 17.2°C (avg) and 24.8°C (peak)"
    const wbKeywordRe = /\bwet[-\s]*bulb\b/gi;
    while ((m = wbKeywordRe.exec(clause)) !== null) {
      const rawTail = clause.slice(m.index + m[0].length, m.index + m[0].length + 110);
      if (/^\s+depression\b/i.test(rawTail)) continue;

      // If "wet-bulb" is a post-modifier of a preceding temperature (e.g. "24.2°C mean wet bulb with the 37.6°C SED peak dry bulb"),
      // do not treat it as a forward-looking header unless followed immediately by a value-introducing copula/preposition.
      const beforeKeywordFull = clause.slice(0, m.index);
      if (
        /\d+(?:\.\d+)?\s*°\s*C\s*(?:\(\s*)?(?:(?:TMY|hottest(?:[-\s]+\d+[-\s]*hour(?:s)?|[-\s]+hours)?|coincident|paired|avg|average|mean|peak|max|maximum|design|monthly|summer|ambient|historical|screening)\s+)*$/i.test(
          beforeKeywordFull,
        ) &&
        !/^\s*(?:temperature(?:s)?\s+)?(?:is|are|was|were|of|at|reaches|reaching|averaging|averages|near|around|:|=|\()/i.test(
          rawTail,
        )
      ) {
        continue;
      }

      const preKeyword = clause.slice(Math.max(0, m.index - 24), m.index);
      const preKeywordWide = clause.slice(Math.max(0, m.index - 70), m.index + m[0].length);
      const isTmyKeyword = isTmyClause || tmyModifierRe.test(preKeywordWide);
      const isPairedDryFirst = /\bdry(?:[-\s]*bulb)?\s*(?:\/|and|&)\s*$/i.test(preKeyword);

      // Stop before any transition to a dry-bulb or contrasting clause
      const barrierMatch =
        /\b(?:dry[-\s]*bulb|summer\s+peak\s+dry|ambient\s+dry|whereas|while|although|however|but\s+(?:peak\s+|summer\s+)?(?:dry|ambient)|versus|vs\.?)\b/i.exec(
          rawTail,
        );
      const boundedTail = barrierMatch ? rawTail.slice(0, barrierMatch.index) : rawTail;

      const tempRe = /(\d+(?:\.\d+)?)\s*°\s*C\b/gi;
      let tm: RegExpExecArray | null;
      let tempOrdinal = 0;
      while ((tm = tempRe.exec(boundedTail)) !== null) {
        tempOrdinal += 1;
        // In "dry/wet-bulb temperatures reach 39.4°C / 23.8°C", the first temperature
        // belongs to dry-bulb and the second belongs to wet-bulb.
        if (isPairedDryFirst && tempOrdinal === 1) continue;
        const afterTemp = rawTail.slice(tm.index + tm[0].length, tm.index + tm[0].length + 48);
        const beforeTemp = rawTail.slice(Math.max(0, tm.index - 35), tm.index);
        // Ignore if the number itself is labeled as dry-bulb, ambient, water/approach/threshold, or DB before/after °C
        if (
          /^\s*(?:\(\s*)?(?:(?:SED|NREL|NSRDB|TMY|NOAA|ASHRAE|historical|annual|record|site|summer|extreme|design|peak|max|maximum|avg|average|mean|coincident|noncoincident|paired|proxy)\s+)*(?:dry[-\s]*bulb|DB\b|ambient\b|supply\s+water|facility[-\s]*water|approach\b)/i.test(
            afterTemp,
          ) ||
          /\b(?:dry[-\s]*bulb|DB|ambient|air\s+temp|supply\s+water|facility[-\s]*water|approach|coil[-\s]*entering|setpoint|threshold)\b/i.test(
            beforeTemp,
          )
        ) {
          continue;
        }
        const val = parseFloat(tm[1]);
        if (isTmyKeyword || tmyModifierRe.test(beforeTemp) || tmyModifierRe.test(afterTemp)) {
          addTmy(val);
        } else {
          addStandard(val);
        }
      }
    }
  }

  return { standard, tmyHottestHours };
}

/**
 * Extracts Celsius temperatures explicitly attributed to annual/monthly/design wet-bulb
 * conditions, without crossing sentence/JSON boundaries, misattributing dry-bulb / ambient
 * temperatures, or conflating TMY hottest-hours sub-period statistics.
 */
export function extractWetBulbMentionsCelsius(text: string): number[] {
  return extractWetBulbMentionsByScope(text).standard;
}

/**
 * Extracts Celsius temperatures explicitly attributed to TMY hottest-hours / paired-mean
 * wet-bulb conditions so they can be checked against `tmyHottestHours.meanWetBulbC`.
 */
export function extractTmyWetBulbMentionsCelsius(text: string): number[] {
  return extractWetBulbMentionsByScope(text).tmyHottestHours;
}

